import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { footerButtonHeight, GhostButton, PrimaryButton } from "../../modalPrimitives";

export interface ClientsSummary {
  /** „Маса 22 след отстъпка" / „след надбавка" / „без корекция". */
  label: string;
  beforeCents: number;
  afterCents: number;
}

/**
 * Футърът на „Клиенти" — по модела на „Отстъпки" (`ItemDiscountsFooter`):
 * обобщението вляво („Маса 22 след отстъпка −8.98" и „~~35.90~~ → 26.92 €"),
 * „Назад" и бутонът вдясно. На телефон обобщението е на собствен ред над бутоните.
 * Без избран клиент обобщение няма.
 */
export function ClientsFooter({
  summary,
  primaryLabel,
  canApply,
  applying,
  onBack,
  onApply,
  isNarrow,
  isDesktop,
}: {
  summary: ClientsSummary | null;
  primaryLabel: ReactNode;
  canApply: boolean;
  applying: boolean;
  onBack: () => void;
  onApply: () => void;
  isNarrow: boolean;
  isDesktop: boolean;
}) {
  const { t } = useTranslation();
  const buttonHeight = footerButtonHeight(isNarrow);
  const format = (cents: number) => (cents / 100).toFixed(2);

  const summaryBlock = summary && (() => {
    const deltaCents = summary.afterCents - summary.beforeCents;
    const changed = deltaCents !== 0;
    const deltaText = (
      <span
        className={`shrink-0 font-pos-mono text-[20px] font-bold leading-[1.2] tabular-nums ${
          changed ? "text-pos-disc-ink" : "text-pos-muted"
        }`}
      >
        {changed ? `${deltaCents < 0 ? "−" : "+"}${format(Math.abs(deltaCents))}` : "0.00"}
      </span>
    );
    const labelText = (
      <span className="truncate text-[12.5px] font-semibold text-pos-soft2">{summary.label}</span>
    );
    const amountsText = (
      <span className="whitespace-nowrap font-pos-mono text-[12.5px] tabular-nums text-pos-soft2">
        {changed && (
          <>
            <span className="text-pos-muted line-through">{format(summary.beforeCents)}</span>
            {" → "}
          </>
        )}
        <span className="font-bold text-pos-ink">{format(summary.afterCents)}</span> €
      </span>
    );
    // На телефон надписът и сумите са един под друг вляво, разликата — вдясно,
    // за да не се реже надписът.
    if (isNarrow) {
      return (
        <div data-testid="clients-footer-summary" className="flex min-w-0 items-center justify-between gap-3">
          <span className="flex min-w-0 flex-col gap-0.5">
            {labelText}
            {amountsText}
          </span>
          {deltaText}
        </div>
      );
    }
    return (
      <div data-testid="clients-footer-summary" className="flex min-w-0 flex-col justify-center gap-0.5">
        <span className="flex min-w-0 items-baseline gap-2">
          {labelText}
          {deltaText}
        </span>
        {amountsText}
      </div>
    );
  })();

  const buttons = (
    <>
      <GhostButton
        label={t("common.back")}
        onClick={onBack}
        shortcut={isDesktop ? "Esc" : undefined}
        height={buttonHeight}
      />
      <PrimaryButton
        label={primaryLabel}
        onClick={onApply}
        disabled={!canApply}
        loading={applying}
        shortcut={isDesktop ? "⏎" : undefined}
        height={buttonHeight}
      />
    </>
  );

  if (isNarrow) {
    return (
      <div className="flex flex-col gap-2.5 border-t border-pos-line2 bg-pos-card px-4 py-3">
        {summaryBlock}
        <div className="grid grid-cols-[1fr_2fr] gap-2">{buttons}</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,0.5fr)_minmax(0,1fr)] items-center gap-2.5 border-t border-pos-line2 bg-pos-card px-[22px] py-3.5">
      {/* Празната клетка пази бутоните вдясно и без избран клиент. */}
      {summaryBlock ?? <div />}
      {buttons}
    </div>
  );
}
